import type { SchemaField } from './api';

// A9 — A FIELD THAT ONLY APPLIES WHEN ANOTHER FIELD SAYS SO.
//
// ===================================================================
// ⚑ THIS IS A CORE GAP WORN AS A WEB MODULE, AND IT SAYS SO
// ===================================================================
// `aa_role.department` is meaningful only when `discipline` is `crew`. A
// picklist of fourteen departments on a lead role is noise on the form a
// person fills in most often, and offering it invites somebody to file an
// acting credit under Camera.
//
// The manifest has NO WAY TO EXPRESS THAT. A FieldDefinition can be required
// or not, and nothing else; there is no `applies_when`. So the rule lives
// here, in the web, where it can be enforced today — and it is filed on
// [[context-vowric-core-engine-gaps-and-upgrades-owed]] as what it actually
// is: a declaration the pack should be making and cannot.
//
// WHY IT IS A TABLE AND NOT AN `if` IN RecordForm. RecordForm is the generic
// engine-driven form and must not learn a pack's vocabulary. It asks this
// module a question about a field it happens to be rendering; the answer, and
// every pack fact behind it, stays here. When the manifest grows the real
// declaration, this file is deleted and RecordForm does not change.
//
// AND IT ONLY EVER HIDES AN EMPTY FIELD. A record that already carries a
// value for a hidden field still shows it — otherwise a role switched from
// crew back to performance would keep a department nobody could see, edit or
// clear. Hiding data is a different act from not offering it.

/** One conditional rule: `field` on `typeKey` is offered only when `when`
 * says so, given the form's current values. */
interface Rule {
  typeKey: string;
  field: string;
  when: (values: Record<string, unknown>) => boolean;
}

const RULES: Rule[] = [
  {
    typeKey: 'aa_role',
    field: 'department',
    // Mirrors lib/discipline.ts's default exactly: absent and unrecognised
    // both mean performance. Written out rather than imported because
    // disciplineOf takes a record and this takes a form's draft values —
    // and a form's draft is not a record until it saves.
    when: (values) => values.discipline === 'crew',
  },
];

/** Should this field be offered on the form right now?
 *
 * `current` is the value already on the record: a non-empty one always shows,
 * so a rule can never strand data. */
export function fieldApplies(
  typeKey: string,
  field: Pick<SchemaField, 'name'>,
  values: Record<string, unknown>
): boolean {
  const rule = RULES.find((r) => r.typeKey === typeKey && r.field === field.name);
  if (!rule) return true;
  if (rule.when(values)) return true;
  const current = values[field.name];
  return current !== undefined && current !== null && current !== '';
}
